// Google Play Billing — JS side of the native PlayBilling plugin (Android only).
// Entitlements are verified server-side (/api/billing/google/*), like Apple.
import { registerPlugin } from "@capacitor/core";
import { api, getAuthToken } from "@/lib/http";
import { isAndroidNative } from "@/lib/platform";

const PlayBilling = registerPlugin("PlayBilling");

export const PRODUCTS = {
  monthly: "solano_pro_monthly",
  yearly: "solano_pro_yearly",
};

/** [{id, basePlanId, offerToken, displayPrice, period: 'month'|'year'}] — one entry per base plan/offer. */
export async function loadGoogleProducts() {
  let lastErr = null;
  for (let attempt = 1; attempt <= 3; attempt++) {
    try {
      const res = await PlayBilling.products({ productIds: Object.values(PRODUCTS) });
      if (res?.products?.length) return res.products;
      lastErr = new Error("EMPTY_PRODUCTS");
    } catch (e) {
      console.error(`[PlayBilling] products() attempt ${attempt} failed:`, e?.message || e);
      lastErr = e;
    }
    if (attempt < 3) await new Promise((r) => setTimeout(r, attempt * 1500));
  }
  throw lastErr || new Error("EMPTY_PRODUCTS");
}

async function submitPurchaseToken(purchaseToken) {
  const { data } = await api.post("/billing/google/purchases", { purchaseToken });
  return data;
}

/** Full purchase flow: account id → Play sheet → server verification.
 *  Returns {status: 'success'|'cancelled'|'pending'|'already_owned'|'error', active?} */
export async function purchaseGoogleSubscription(product) {
  const { data: tok } = await api.post("/billing/google/account-token");
  const res = await PlayBilling.purchase({ productId: product.id, offerToken: product.offerToken, obfuscatedAccountId: tok.obfuscatedAccountId });
  if (res?.status === "success" && res.purchase?.purchaseToken) {
    const verified = await submitPurchaseToken(res.purchase.purchaseToken);
    return { status: "success", active: verified?.active };
  }
  if (res?.status === "already_owned") {
    const r = await restoreGooglePurchases();
    return { status: r.active ? "success" : "already_owned", active: r.active > 0 };
  }
  return { status: res?.status || "cancelled" };
}

/** "Käufe wiederherstellen" / foreground reconciliation: re-submit all current Play purchases. */
export async function restoreGooglePurchases() {
  const { purchases } = await PlayBilling.entitlements();
  let active = 0;
  for (const p of purchases || []) {
    if (!p.purchaseToken || p.purchaseState !== "purchased") continue;
    try {
      const r = await submitPurchaseToken(p.purchaseToken);
      if (r?.active) active += 1;
    } catch { /* one bad token must not block the rest */ }
  }
  return { restored: (purchases || []).length, active };
}

export async function manageGoogleSubscription(productId) {
  await PlayBilling.manage({ productId: productId || undefined });
}

let reconcileStarted = false;
/** App launch (Android): re-submit any owned Play purchases so a purchase that the
 *  server could not verify at buy time (offline, server not configured) still
 *  gets acknowledged and unlocks Pro — Apple's transaction listener equivalent. */
export function initGoogleBillingReconcile() {
  if (!isAndroidNative() || reconcileStarted) return;
  reconcileStarted = true;
  const run = () => {
    if (!getAuthToken()) return;
    restoreGooglePurchases()
      .then((r) => { if (r.active) window.dispatchEvent(new CustomEvent("solano:plan-changed")); })
      .catch(() => {});
  };
  setTimeout(run, 4000);
  document.addEventListener("visibilitychange", () => { if (document.visibilityState === "visible") run(); });
}

export async function playBillingDiagnostics() {
  try {
    const res = await PlayBilling.products({ productIds: Object.values(PRODUCTS) });
    return { ok: true, loaded: (res?.products || []).map((p) => `${p.id}/${p.basePlanId} ${p.displayPrice}`) };
  } catch (e) {
    return { ok: false, error: String(e?.message || e), code: e?.code || null };
  }
}
